import { useEffect, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CheckCircle2, MessageCircle, Phone, ClipboardCheck, Clock3, ShieldCheck, CalendarDays } from "lucide-react";
import { useLang } from "@/context";
import { CONTACT, waLink, APPLIANCE_VALUES } from "@/i18n";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRESELECT = {
    ac: "AC",
    fridge: "Refrigerator",
    microwave: "Microwave",
    washing: "Washing Machine",
    geyser: "Geyser / Water Heater",
    ro: "RO Water Purifier",
};
const INFO_ICONS = [ClipboardCheck, ShieldCheck, Clock3];

export const BookingForm = () => {
    const { t, lang } = useLang();
    const f = t.form;
    const empty = { full_name: "", phone: "", area_pin: "", appliance_type: "", issue_description: "", preferred_time: "" };
    const [form, setForm] = useState(empty);
    const [visitDate, setVisitDate] = useState(null);
    const [calendarOpen, setCalendarOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        const handler = (e) => {
            const value = PRESELECT[e.detail];
            if (value) {
                setForm((p) => ({ ...p, appliance_type: value }));
                setDone(false);
            }
        };
        window.addEventListener("preselect-appliance", handler);
        return () => window.removeEventListener("preselect-appliance", handler);
    }, []);

    const set = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!visitDate) {
            toast.error(f.needDate);
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/bookings`, { ...form, preferred_date: format(visitDate, "yyyy-MM-dd"), language: lang });
            setDone(true);
            toast.success(f.successTitle);
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : f.error);
        } finally {
            setSubmitting(false);
        }
    };

    const waText = `Hi Brighten Services, I just submitted a service request.\nName: ${form.full_name}\nAppliance: ${form.appliance_type}\nArea/PIN: ${form.area_pin}\nIssue: ${form.issue_description}\nPreferred: ${visitDate ? format(visitDate, "yyyy-MM-dd") : ""} ${form.preferred_time}`;

    return (
        <section id="book" data-testid="booking-section" className="py-20 sm:py-28 bg-[#F3E9E2]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-8">
                <Reveal className="lg:col-span-2">
                    <div className="h-full rounded-3xl bg-[#2A0505] text-[#F7EEE8] p-8 sm:p-10 flex flex-col">
                        <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight">{f.heading}</h2>
                        <p className="text-[#D9C4BB] mt-3">{f.sub}</p>
                        <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#C9A24B] mt-8">{f.infoTitle}</p>
                        <ul className="mt-4 space-y-4 flex-1">
                            {f.infoPoints.map((point, i) => {
                                const Icon = INFO_ICONS[i % INFO_ICONS.length];
                                return (
                                    <li key={point} className="flex gap-3 items-start">
                                        <span className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                                            <Icon className="w-[18px] h-[18px] text-[#C9A24B]" />
                                        </span>
                                        <span className="text-sm sm:text-base leading-relaxed">{point}</span>
                                    </li>
                                );
                            })}
                        </ul>
                        <div className="mt-8 pt-6 border-t border-white/10 flex flex-wrap gap-3">
                            <a href={`tel:${CONTACT.phone1}`} data-testid="booking-info-call" className="inline-flex items-center gap-2 bg-[#8D1717] hover:bg-[#6D1010] text-white text-sm font-semibold rounded-full px-5 py-3 transition-colors">
                                <Phone className="w-4 h-4" />{CONTACT.phone1}
                            </a>
                            <a href={waLink()} target="_blank" rel="noreferrer" data-testid="booking-info-whatsapp" className="inline-flex items-center gap-2 bg-[#22C55E] hover:bg-[#16A34A] text-white text-sm font-semibold rounded-full px-5 py-3 transition-colors">
                                <MessageCircle className="w-4 h-4" />{t.cta.whatsapp}
                            </a>
                        </div>
                    </div>
                </Reveal>

                <Reveal delay={0.15} className="lg:col-span-3">
                    <div className="h-full rounded-3xl bg-white border border-[#EADFD8] shadow-xl shadow-[#2A0505]/5 p-8 sm:p-10">
                        {done ? (
                            <div data-testid="booking-success-message" className="h-full flex flex-col items-center justify-center text-center py-10">
                                <CheckCircle2 className="w-16 h-16 text-[#22C55E]" />
                                <h3 className="font-display text-2xl sm:text-3xl font-bold text-[#2A0505] mt-6">{f.successTitle}</h3>
                                <p className="text-sm sm:text-base text-[#6B5B57] mt-3 max-w-md leading-relaxed">{f.successText}</p>
                                <div className="flex flex-wrap justify-center gap-3 mt-8">
                                    <a href={waLink(waText)} target="_blank" rel="noreferrer" data-testid="booking-whatsapp-followup" className="inline-flex items-center gap-2 bg-[#22C55E] hover:bg-[#16A34A] text-white font-semibold rounded-full px-6 py-3.5 transition-colors">
                                        <MessageCircle className="w-5 h-5" />{f.successWhatsApp}
                                    </a>
                                    <button
                                        data-testid="booking-new-request-button"
                                        onClick={() => {
                                            setForm(empty);
                                            setVisitDate(null);
                                            setDone(false);
                                        }}
                                        className="inline-flex items-center gap-2 border-2 border-[#EADFD8] hover:border-[#8D1717] hover:text-[#8D1717] text-[#2A0505] font-semibold rounded-full px-6 py-3 transition-colors"
                                    >
                                        {f.newRequest}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <form onSubmit={submit} data-testid="appliance-booking-request-form" className="space-y-5">
                                <div className="grid sm:grid-cols-2 gap-5">
                                    <div className="space-y-2">
                                        <Label htmlFor="bf-name">{f.labels.fullName} *</Label>
                                        <Input id="bf-name" data-testid="booking-input-full-name" required minLength={2} value={form.full_name} onChange={set("full_name")} placeholder={f.placeholders.fullName} className="h-12 rounded-xl" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="bf-phone">{f.labels.phone} *</Label>
                                        <Input id="bf-phone" data-testid="booking-input-phone" required type="tel" minLength={8} value={form.phone} onChange={set("phone")} placeholder={f.placeholders.phone} className="h-12 rounded-xl" />
                                    </div>
                                </div>
                                <div className="grid sm:grid-cols-2 gap-5">
                                    <div className="space-y-2">
                                        <Label htmlFor="bf-area">{f.labels.areaPin} *</Label>
                                        <Input id="bf-area" data-testid="booking-input-area-pin" required minLength={3} value={form.area_pin} onChange={set("area_pin")} placeholder={f.placeholders.areaPin} className="h-12 rounded-xl" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>{f.labels.appliance} *</Label>
                                        <Select required value={form.appliance_type} onValueChange={(v) => setForm((p) => ({ ...p, appliance_type: v }))}>
                                            <SelectTrigger data-testid="booking-select-appliance" className="h-12 rounded-xl">
                                                <SelectValue placeholder={f.placeholders.appliance} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {APPLIANCE_VALUES.map((v, i) => (
                                                    <SelectItem key={v} value={v} data-testid={`booking-appliance-option-${i}`}>
                                                        {f.applianceOptions[i] || v}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="bf-issue">{f.labels.issue} *</Label>
                                    <Textarea id="bf-issue" data-testid="booking-input-issue" required minLength={3} rows={4} value={form.issue_description} onChange={set("issue_description")} placeholder={f.placeholders.issue} className="rounded-xl" />
                                </div>
                                <div className="grid sm:grid-cols-2 gap-5">
                                    <div className="space-y-2">
                                        <Label>{f.labels.date} *</Label>
                                        <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                                            <PopoverTrigger asChild>
                                                <button
                                                    type="button"
                                                    data-testid="booking-date-picker"
                                                    className={`w-full h-12 rounded-xl border border-input bg-background px-3 flex items-center gap-2 text-sm text-left transition-colors hover:border-[#8D1717] ${visitDate ? "text-foreground" : "text-muted-foreground"}`}
                                                >
                                                    <CalendarDays className="w-4 h-4 text-[#8D1717] shrink-0" />
                                                    {visitDate ? format(visitDate, "PPP") : f.placeholders.date}
                                                </button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-auto p-0" align="start">
                                                <Calendar
                                                    mode="single"
                                                    selected={visitDate}
                                                    onSelect={(d) => {
                                                        setVisitDate(d);
                                                        setCalendarOpen(false);
                                                    }}
                                                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                                                    initialFocus
                                                />
                                            </PopoverContent>
                                        </Popover>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>{f.labels.time} *</Label>
                                        <Select required value={form.preferred_time} onValueChange={(v) => setForm((p) => ({ ...p, preferred_time: v }))}>
                                            <SelectTrigger data-testid="booking-select-time" className="h-12 rounded-xl">
                                                <SelectValue placeholder={f.placeholders.time} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {f.timeOptions.map((opt) => (
                                                    <SelectItem key={opt} value={opt} data-testid={`booking-time-option-${opt}`}>
                                                        {opt}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <p className="text-xs text-[#a08b85] leading-relaxed" data-testid="booking-privacy-notice">{f.privacy}</p>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="booking-form-submit-button"
                                    className="w-full inline-flex items-center justify-center gap-2 bg-[#8D1717] hover:bg-[#6D1010] disabled:opacity-60 text-white font-bold rounded-full px-8 py-4 text-base shadow-lg shadow-[#8D1717]/25 transition-colors"
                                >
                                    {submitting ? f.submitting : f.submit}
                                </button>
                            </form>
                        )}
                    </div>
                </Reveal>
            </div>
        </section>
    );
};
